import React from 'react';
import { MapPin, Phone, Mail, Facebook, Instagram } from 'lucide-react';
import { restaurantInfo } from '../data/mock';
import { useLanguage } from '../context/LanguageContext';

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const { t } = useLanguage();

  const scrollToSection = (href) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const navItems = [
    { label: t('nav.home'), href: '#home' },
    { label: t('nav.about'), href: '#about' },
    { label: t('nav.menu'), href: '#menu' },
    { label: t('nav.gallery'), href: '#gallery' },
    { label: t('nav.contact'), href: '#contact' }
  ];

  return (
    <footer className="bg-stone-950 border-t border-stone-800 relative overflow-hidden">
      {/* Decorative gradient */}
      <div className="absolute inset-0 bg-gradient-to-t from-amber-950/20 to-transparent pointer-events-none" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 relative">
        <div className="grid md:grid-cols-4 gap-10">
          {/* Brand */}
          <div className="md:col-span-2">
            <h3 className="font-serif text-3xl font-bold text-amber-100 mb-4">
              Trattoria Da Daniele
            </h3>
            <p className="text-amber-100/60 leading-relaxed mb-6 max-w-md">
              {t('footer.description')}
            </p>
            {/* Social Links */}
            <div className="flex gap-4">
              <a
                href="https://www.facebook.com/profile.php?id=100007687037483"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Facebook"
                data-testid="social-facebook"
                className="w-10 h-10 bg-stone-800 hover:bg-amber-700 rounded-full flex items-center justify-center text-amber-100/70 hover:text-white transition-all"
              >
                <Facebook className="w-5 h-5" />
              </a>
              <a
                href="https://www.instagram.com/explore/locations/252749598/ristorante-trattoria-da-daniele/"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Instagram"
                data-testid="social-instagram"
                className="w-10 h-10 bg-stone-800 hover:bg-amber-700 rounded-full flex items-center justify-center text-amber-100/70 hover:text-white transition-all"
              >
                <Instagram className="w-5 h-5" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-white font-semibold mb-4 uppercase tracking-wider text-sm">
              {t('footer.menuTitle')}
            </h4>
            <ul className="space-y-3">
              {navItems.map((item) => (
                <li key={item.href}>
                  <button
                    onClick={() => scrollToSection(item.href)}
                    className="text-amber-100/60 hover:text-amber-400 transition-colors text-sm"
                  >
                    {item.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="text-white font-semibold mb-4 uppercase tracking-wider text-sm">
              {t('footer.contactTitle')}
            </h4>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                <span className="text-amber-100/60 text-sm">{restaurantInfo.address}</span>
              </li>
              <li className="flex items-center gap-3">
                <Phone className="w-5 h-5 text-amber-500 flex-shrink-0" />
                <a href="tel:+41326236888" className="text-amber-100/60 hover:text-amber-400 transition-colors text-sm">
                  +41 32 623 68 88
                </a>
              </li>
              <li className="flex items-center gap-3">
                <Mail className="w-5 h-5 text-amber-500 flex-shrink-0" />
                <a href={`mailto:${restaurantInfo.email}`} className="text-amber-100/60 hover:text-amber-400 transition-colors text-sm">
                  {restaurantInfo.email}
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-stone-800 mt-12 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-amber-100/50 text-sm order-1">
            © {currentYear} Trattoria Da Daniele. {t('footer.rights')}
          </p>

          <a
            href="https://doubleastudio.ch"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="credits-double-a-studio"
            className="group flex items-center gap-2 px-4 py-1.5 rounded-full bg-stone-900/60 border border-stone-800 hover:border-amber-600/50 hover:bg-stone-900 transition-all duration-300 order-3 md:order-2"
          >
            <span className="text-amber-100/50 text-[10px] tracking-wider uppercase group-hover:text-amber-100/80 transition-colors">
              Designed &amp; Developed by
            </span>
            <img
              src="/images/credits/double-a-studio-logo.png"
              alt="Double A Studio"
              className="w-5 h-5 rounded-full object-cover ring-1 ring-stone-700 group-hover:ring-amber-600/60 transition-all"
            />
            <span className="font-serif text-amber-100/80 text-xs font-medium group-hover:text-amber-300 transition-colors">
              Double A Studio
            </span>
          </a>

          <p className="text-amber-100/40 text-xs order-2 md:order-3">
            Goldgasse 15, 4500 Solothurn, Schweiz
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
